import { useEffect, useState } from "react";
import FormCheck from 'react-bootstrap/FormCheck';

import AliasListDisplay from "./BenchmarkAliasDisplay";
import BenchmarkTradeDisplay from "./BenchmarkTradeDisplay";

import { reliabilityEngineerAccess } from "../../functions/AccessHelpers";

import EmployeeInfo from "../../types/Employee/EmployeeInfo";
import { Activity, EMPTY_ACTIVITY } from "../../types/Quality/Activity";
import { Trade } from "../../types/Quality/Trade";

type EditBenchmarkActivityProps = {
    activity: Activity | undefined,
    allTrades: Trade[],
    onChange: (activity: Activity) => void,
    userData: EmployeeInfo
};

const EditBenchmarkActivity = ({ activity, allTrades, onChange, userData }: EditBenchmarkActivityProps) => {
    const [currentActivity, setCurrentActivity] = useState<Activity>(activity ?? EMPTY_ACTIVITY);

    /**
     * this will force the component rerender on any changes to the activity
     *  data being set by setCurrentActivity
     */
    useEffect(() => {
        onChange(currentActivity);
    }, [currentActivity, onChange]);

    const handleNameInput = (name: string) => {
        const newActivity = {...currentActivity};
        newActivity.name = name;
        setCurrentActivity(newActivity);
    };

    const handleTradesChange = (trades: Trade[]) => {
        const newActivity = {...currentActivity};
        newActivity.trades = trades;
        setCurrentActivity(newActivity);
    };

    const handleActiveToggle = () => {
        const newActivity = {...currentActivity};
        newActivity.isActive = !currentActivity.isActive;
        setCurrentActivity(newActivity);
    };

    const handleLockToggle = () => {
        const newActivity = {...currentActivity};
        newActivity.isLocked = !currentActivity.isLocked;
        setCurrentActivity(newActivity);
    };

    /**
     * this is a stand in for later functionality where users can make the
     *   updates to the Active status based off of user permissions 
     *   for now we will always return false until the UI functionality
     *   is augmented to allow this update
     * @returns false
     */
    const canEditActive = (userData: EmployeeInfo) => (
        reliabilityEngineerAccess(userData.jobCode)
    );

    /**
     * this is a stand in for later functionality where users can make the
     *   updates to the Lock status based off of user permissions 
     *   for now we will always return false until the UI functionality
     *   is augmented to allow this update
     * @returns 
     */
    const canEditLock = (userData: EmployeeInfo) => (
        reliabilityEngineerAccess(userData.jobCode)
    );

    return (
        <div className='col'>
            <div className='row my-2 py-1 border-bottom'>
                <div className='col-3 border-end my-1 pb-1 py-1'>
                    Name
                </div>
                <div className='col'>
                    <input className='border rounded-2 my-1 p-1 w-100'
                        style={{ textTransform: 'uppercase'}}
                        type='text'
                        value={currentActivity.name}
                        onChange={(e) => handleNameInput(e.target.value.trimStart())}
                        disabled={currentActivity.isLocked || !currentActivity.isActive}
                    />
                </div>
            </div>
            { false &&
                <div className='row my-2 py-1 border-bottom'>
                    <div className='col-3 border-end'>
                       Aliases 
                    </div>
                    <div className='col'>
                        <AliasListDisplay
                            aliases={currentActivity.aliases}
                        />
                    </div>
                </div>
            }
            <div className='row my-2 py-1 border-bottom'>
                <div className='col-3 border-end'>
                    Trades
                </div>
                <div className='col'>
                    <BenchmarkTradeDisplay
                        trades={currentActivity?.trades}
                        allTrades={allTrades}
                        editable={currentActivity.isActive && !currentActivity.isLocked}
                        listOnly={false}
                        onTradesChange={(t) => handleTradesChange(t)}
                    />
                </div>
            </div>
            <div
                className='row my-2 py-1 border-bottom'
                title='Only active activities can be selected for use with Benchmark Goals.  Reliability Engineers can change activity active status.'
            >
                <div className='col-3 border-end'>
                    {currentActivity.isActive ? 'Active' : 'Inactive'}
                </div>
                <div className='col'>
                    <FormCheck
                        type='checkbox'
                        id='status'
                        label='Is Active'
                        checked={currentActivity.isActive}
                        onChange={() => handleActiveToggle()}
                        disabled={!canEditActive(userData)}
                    />
                </div>
            </div>
            <div
                className='row my-2'
                title='Locked activities cannot be edited.  Reliability Engineers can change activity lock status.'
            >
                <div className='col-3 border-end'>
                    {currentActivity.isLocked ? 'Locked' : 'Unlocked'}
                </div>
                <div className='col'>
                    <FormCheck
                        type='checkbox'
                        id='status'
                        label='Is Locked'
                        checked={currentActivity.isLocked}
                        onChange={() => handleLockToggle()}
                        disabled={!canEditLock(userData)}
                    />
                </div>
            </div>
        </div>
    );
};

export default EditBenchmarkActivity;